import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import PageLayout from '../layout/PageLayout';
import { Loading, PanelSkeleton } from '../layout/Skeletons';
import { useAuth } from '../../context/AuthContext';

/**
 * A screen that has nothing to show without an account.
 *
 * It stands in front of the page rather than inside it, so the page's own
 * hooks never run for somebody who is not signed in: they would ask the API
 * for "my listing" or "my appointments", be told 401, and turn that into an
 * error message about something the visitor never did wrong.
 *
 * Where it sends them is the point. A visitor who followed "inscrire mon
 * activité" has no account at all, so /login would be a dead end — they are
 * sent to sign up. Somebody reaching a screen that only makes sense once you
 * have one is more likely to have lost a session, so they are sent to sign in.
 * Either way the address they asked for is kept, and the form sends them back
 * to it.
 */
function RequireAccount({ to = '/login', children }) {
  const { isAuthenticated, loading } = useAuth();
  const { pathname, search } = useLocation();

  if (loading) {
    return (
      <PageLayout centered={false}>
        <Loading label="Chargement">
          <Box sx={{ width: '100%' }}>
            <PanelSkeleton height={420} />
          </Box>
        </Loading>
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`${to}?next=${encodeURIComponent(pathname + search)}`} replace />;
  }

  return children;
}

export default RequireAccount;
